import { useEffect, useRef, useState } from 'react';

export function useAnimatedNumber(value: number, duration = 800) {
	const frameRef = useRef<number | null>(null);
	const animatedValueRef = useRef(value);
	const [animatedValue, setAnimatedValue] = useState(value);

	useEffect(() => {
		const startValue = animatedValueRef.current;
		const startTime = performance.now();

		if (frameRef.current !== null) {
			cancelAnimationFrame(frameRef.current);
		}

		const updateValue = (now: number) => {
			const elapsed = now - startTime;
			const progress = Math.min(elapsed / duration, 1);
			const easedProgress = 1 - Math.pow(1 - progress, 3);

			const nextValue = startValue + (value - startValue) * easedProgress;
			animatedValueRef.current = nextValue;
			setAnimatedValue(nextValue);

			if (progress < 1) {
				frameRef.current = requestAnimationFrame(updateValue);
			} else {
				frameRef.current = null;
			}
		};

		frameRef.current = requestAnimationFrame(updateValue);

		return () => {
			if (frameRef.current !== null) {
				cancelAnimationFrame(frameRef.current);
			}
		};
	}, [duration, value]);

	return animatedValue;
}
